/**
 * A* pathfinding over the hex grid.
 *
 * Two things about this are worth understanding before changing it.
 *
 * FIRST, IT RUNS ON WHAT THE CIVILISATION KNOWS, not on the world as it is. Costs are
 * read through a vision plane; unexplored tiles are assumed passable at the cheapest
 * rate. That makes a unit walk hopefully toward the unknown and discover the mountain
 * range the hard way, which is both how exploration should feel and the only honest
 * option — a path routed around terrain the player has never seen would hand them
 * knowledge their units have not earned. Pass `vision: null` for an omniscient path
 * (the map tools and tests use it; game code should not).
 *
 * SECOND, IT IS DETERMINISTIC. The heuristic is admissible, neighbours are always
 * examined in the same direction order, and MinHeap breaks priority ties by insertion
 * order. Identical inputs give a byte-identical path, which is what lets a turn's
 * state hash be compared across runs.
 */

import type { Content, UnitDef } from '../../../content/schema/index.js';
import { hexDistance, offsetToAxial } from '../hex/index.js';
import { indexToOffset, neighborsByDirection } from '../state/grid.js';
import { MinHeap } from '../util/heap.js';
import { VIS_UNKNOWN, type TileGrid } from '../state/types.js';
import { cheapestMoveCost, IMPASSABLE, moveCost } from './movement.js';

export interface PathOptions {
  content: Content;
  map: TileGrid;
  def: UnitDef;
  /** The mover's knowledge, or null to path with perfect information. */
  vision: Uint8Array | null;
  /**
   * Tiles to route around as if impassable — someone else's units or city. Movement
   * rules are about ground; who is standing on it is the caller's business, so it
   * arrives as a question rather than as state the pathfinder would have to read.
   */
  avoid?: (index: number) => boolean;
  /** Land tiles a ship may enter: the mover's own cities. */
  port?: (index: number) => boolean;
  /** A step that is forbidden although both tiles are enterable — zones of control. */
  blockedStep?: (from: number, to: number) => boolean;
}

/**
 * Hex distance that knows the world is a cylinder.
 *
 * Without the lap search a path from one side of the date line to the other gets a
 * heuristic of nearly the whole map width, A* degenerates into Dijkstra, and the
 * search explores the entire world to find a two-tile walk.
 */
export function wrappedHexDistance(map: TileGrid, from: number, to: number): number {
  const a = indexToOffset(map, from);
  const b = indexToOffset(map, to);
  const ah = offsetToAxial(a.col, a.row);

  let best = hexDistance(ah, offsetToAxial(b.col, b.row));
  if (map.wrapX) {
    for (const lap of [-1, 1]) {
      const d = hexDistance(ah, offsetToAxial(b.col + lap * map.width, b.row));
      if (d < best) best = d;
    }
  }
  return best;
}

/** Cost to step from one tile to the next as the mover currently understands it. */
function knownCost(options: PathOptions, index: number, from = -1): number {
  if (options.avoid?.(index)) return IMPASSABLE;
  if (options.vision && options.vision[index] === VIS_UNKNOWN) {
    // Optimistic: unexplored ground is assumed to be walkable and easy.
    return cheapestMoveCost(options.content);
  }
  return moveCost(options.content, options.map, index, options.def, options.port?.(index) ?? false, from);
}

/**
 * The cheapest route from `from` to `to`, as the tiles to be ENTERED — the starting
 * tile is not included. Returns null when no route exists, and an empty array when
 * the unit is already there.
 */
export function findPath(options: PathOptions, from: number, to: number): number[] | null {
  const { map } = options;
  if (from === to) return [];
  if (knownCost(options, to) === IMPASSABLE) return null;

  const tiles = map.width * map.height;
  const gScore = new Float64Array(tiles).fill(IMPASSABLE);
  const cameFrom = new Int32Array(tiles).fill(-1);
  const closed = new Uint8Array(tiles);
  const neighbours = new Int32Array(6);
  const scale = cheapestMoveCost(options.content);

  const open = new MinHeap(Math.min(tiles, 1024));
  gScore[from] = 0;
  open.push(from, wrappedHexDistance(map, from, to) * scale);

  while (!open.isEmpty) {
    const current = open.pop()!;
    if (current === to) return reconstruct(cameFrom, from, to);
    if (closed[current] === 1) continue;
    closed[current] = 1;

    neighborsByDirection(map, current, neighbours);
    for (let d = 0; d < 6; d++) {
      const next = neighbours[d]!;
      if (next === -1 || closed[next] === 1) continue;

      const step = knownCost(options, next, current);
      if (step === IMPASSABLE) continue;
      if (options.blockedStep?.(current, next)) continue;

      const tentative = gScore[current]! + step;
      if (tentative >= gScore[next]!) continue;

      gScore[next] = tentative;
      cameFrom[next] = current;
      open.push(next, tentative + wrappedHexDistance(map, next, to) * scale);
    }
  }

  return null;
}

function reconstruct(cameFrom: Int32Array, from: number, to: number): number[] {
  const reversed: number[] = [];
  let node = to;
  while (node !== from) {
    reversed.push(node);
    node = cameFrom[node]!;
    if (node === -1) return [];
  }
  return reversed.reverse();
}

/**
 * Every tile reachable with the move points a unit has left, mapped to what reaching
 * it would cost. Used for the movement overlay, and later by the AI's tactical layer.
 *
 * Dijkstra rather than A*: there is no single goal, and the "you may always move one
 * tile" rule means the frontier is defined by budget rather than by distance.
 */
export function reachableTiles(options: PathOptions, from: number, movesLeft: number): Map<number, number> {
  const { map } = options;
  const reached = new Map<number, number>();
  if (movesLeft <= 0) return reached;

  const tiles = map.width * map.height;
  const best = new Float64Array(tiles).fill(IMPASSABLE);
  const neighbours = new Int32Array(6);
  const open = new MinHeap(64);

  best[from] = 0;
  open.push(from, 0);

  while (!open.isEmpty) {
    const current = open.pop()!;
    const spent = best[current]!;
    if (spent > movesLeft) continue;

    neighborsByDirection(map, current, neighbours);
    for (let d = 0; d < 6; d++) {
      const next = neighbours[d]!;
      if (next === -1) continue;
      const step = knownCost(options, next, current);
      if (step === IMPASSABLE) continue;
      if (options.blockedStep?.(current, next)) continue;

      // The single-tile rule applies at the frontier: a tile is reachable if the unit
      // still had *something* left when it arrived at the tile before it.
      const affordable = spent + step <= movesLeft || (options.content.rules.movement.allowSingleTileWhenDepleted && spent < movesLeft);
      if (!affordable) continue;

      const total = Math.min(spent + step, movesLeft);
      if (total >= best[next]!) continue;
      best[next] = total;
      reached.set(next, total);
      open.push(next, total);
    }
  }

  reached.delete(from);
  return reached;
}
